import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { 
  Building2, Users, FileText, Send, Printer, Download, Plus, 
  IndianRupee, Calendar, ChevronRight, Eye, Edit2, Archive,
  CheckCircle, Clock, AlertCircle, Home, Mail, X, Trash2, Upload,
  Bell, BellOff, History, Sparkles, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { formatINR, stripCommas } from '../lib/numberFormat';

// Confirmation Dialog Component (replaces browser native confirm)
const ConfirmDialog = ({ open, onOpenChange, title, description, onConfirm, confirmText = "Confirm", variant = "default" }) => {
  const buttonVariants = {
    default: "bg-primary text-white hover:bg-primary/90",
    danger: "bg-red-600 text-white hover:bg-red-700"
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {variant === "danger" && <AlertCircle className="text-red-600" size={20} />}
            {title}
          </DialogTitle>
          <DialogDescription className="text-gray-600 pt-2">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 mt-4">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            data-testid="confirm-cancel-btn"
          >
            Cancel
          </Button>
          <Button 
            className={buttonVariants[variant]}
            onClick={() => { onConfirm(); onOpenChange(false); }}
            data-testid="confirm-ok-btn"
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Status Badge Component (static display)
const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-700',
    sent: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700'
  };
  const icons = {
    pending: <Clock size={12} />,
    sent: <Mail size={12} />,
    paid: <CheckCircle size={12} />
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
      {icons[status]} {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Interactive Status Dropdown Component
const StatusDropdown = ({ invoiceId, currentStatus, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const statuses = ['pending', 'sent', 'paid'];
  const styles = {
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    sent: 'bg-blue-100 text-blue-700 border-blue-300',
    paid: 'bg-green-100 text-green-700 border-green-300'
  };
  const icons = {
    pending: <Clock size={12} />,
    sent: <Mail size={12} />,
    paid: <CheckCircle size={12} />
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus !== currentStatus) {
      await onStatusChange(invoiceId, newStatus);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block" data-testid={`status-dropdown-${invoiceId}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity ${styles[currentStatus]}`}
        data-testid={`status-btn-${invoiceId}`}
      >
        {icons[currentStatus]} 
        {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
        <ChevronRight size={12} className={`transform transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute z-10 mt-1 w-28 bg-white border rounded-lg shadow-lg py-1" data-testid="status-options">
          {statuses.map(status => (
            <button
              key={status}
              onClick={() => handleStatusChange(status)}
              className={`w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 hover:bg-gray-50 ${
                status === currentStatus ? 'font-semibold' : ''
              }`}
              data-testid={`status-option-${status}`}
            >
              {icons[status]}
              {status.charAt(0).toUpperCase() + status.slice(1)}
              {status === currentStatus && <CheckCircle size={10} className="ml-auto text-green-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const RentalManagement = ({ user }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showHistoricalModal, setShowHistoricalModal] = useState(false);
  const [showEditInvoiceModal, setShowEditInvoiceModal] = useState(false);
  
  // Edit states
  const [editingProperty, setEditingProperty] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  
  // Confirmation dialog states
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: () => {}, variant: 'default', confirmText: 'Confirm' });
  
  // Search and Sort states for invoices
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceSort, setInvoiceSort] = useState({ field: 'invoice_date', order: 'desc' });
  
  // Form states
  const [propertyForm, setPropertyForm] = useState({ name: '', address: '', base_rent: '' });
  const [tenantForm, setTenantForm] = useState({ 
    property_id: '', company_name: '', gstin: '', billing_address: '', email: '', contact_person: '', phone: '' 
  });
  const [invoiceForm, setInvoiceForm] = useState({ 
    tenant_id: '', invoice_no: '', alias_invoice_no: '', invoice_date: '', months: [], base_amount: '', sgst_rate: '9', cgst_rate: '9', notes: '' 
  });
  const [historicalForm, setHistoricalForm] = useState({
    tenant_id: '', invoice_no: '', invoice_date: '', months: '', base_amount: '', total_amount: '', notes: ''
  });
  const [ownerSettings, setOwnerSettings] = useState({
    name: '', address: '', pan: '', gstin: '', gst_registration_date: '',
    bank_name: '', account_name: '', account_number: '', ifsc_code: '', branch: '',
    default_sgst_rate: '9', default_cgst_rate: '9', default_notes: '', invoice_terms: ''
  });
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [pdfData, setPdfData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [nextInvoiceNo, setNextInvoiceNo] = useState('');
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [showGenerateResult, setShowGenerateResult] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);

  useEffect(() => {
    fetchData();
    fetchOwnerSettings();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, propRes, tenantRes, invRes] = await Promise.all([
        api.get('/rentals/dashboard'),
        api.get('/rentals/properties'),
        api.get('/rentals/tenants'),
        api.get('/rentals/invoices')
      ]);
      setDashboard(dashRes.data);
      setProperties(propRes.data);
      setTenants(tenantRes.data);
      setInvoices(invRes.data);
    } catch (error) {
      console.error('Error fetching rental data:', error);
      // Don't show error toast if we have existing data (just a refresh failure)
      if (!invoices.length && !properties.length && !tenants.length) {
        toast.error('Failed to load rental data. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnerSettings = async () => {
    try {
      const res = await api.get('/rentals/settings');
      setOwnerSettings(res.data);
    } catch (error) {
      console.error('Error fetching owner settings:', error);
    }
  };

  const handleSaveOwnerSettings = async () => {
    try {
      await api.patch('/rentals/settings', ownerSettings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const fetchNextInvoiceNumber = async () => {
    try {
      const res = await api.get('/rentals/invoices/next-number');
      setNextInvoiceNo(res.data.next_invoice_number);
    } catch (error) {
      console.error('Error fetching invoice number:', error);
    }
  };

  // Property handlers
  const handleCreateProperty = async () => {
    try {
      await api.post('/rentals/properties', {
        ...propertyForm,
        base_rent: parseFloat(propertyForm.base_rent)
      });
      toast.success('Property created successfully');
      setShowPropertyModal(false);
      setPropertyForm({ name: '', address: '', base_rent: '' });
      setEditingProperty(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to create property');
    }
  };

  const handleUpdateProperty = async () => {
    try {
      await api.patch(`/rentals/properties/${editingProperty.id}`, {
        ...propertyForm,
        base_rent: parseFloat(propertyForm.base_rent)
      });
      toast.success('Property updated successfully');
      setShowPropertyModal(false);
      setPropertyForm({ name: '', address: '', base_rent: '' });
      setEditingProperty(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update property');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Property',
      description: 'Are you sure you want to delete this property? This will also affect associated tenants.',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await api.delete(`/rentals/properties/${propertyId}`);
          toast.success('Property deleted');
          fetchData();
        } catch (error) {
          toast.error('Failed to delete property');
        }
      }
    });
  };

  const handleTogglePropertyStatus = async (propertyId, newStatus) => {
    try {
      await api.patch(`/rentals/properties/${propertyId}`, { is_occupied: newStatus });
      toast.success(`Property marked as ${newStatus ? 'Occupied' : 'Available'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update property status');
    }
  };

  const openEditProperty = (property) => {
    setEditingProperty(property);
    setPropertyForm({
      name: property.name,
      address: property.address,
      base_rent: property.base_rent.toString()
    });
    setShowPropertyModal(true);
  };

  // Tenant handlers
  const handleCreateTenant = async () => {
    try {
      await api.post('/rentals/tenants', tenantForm);
      toast.success('Tenant created successfully');
      setShowTenantModal(false);
      setTenantForm({ property_id: '', company_name: '', gstin: '', billing_address: '', email: '', contact_person: '', phone: '' });
      setEditingTenant(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to create tenant');
    }
  };

  const handleUpdateTenant = async () => {
    try {
      await api.patch(`/rentals/tenants/${editingTenant.id}`, tenantForm);
      toast.success('Tenant updated successfully');
      setShowTenantModal(false);
      setTenantForm({ property_id: '', company_name: '', gstin: '', billing_address: '', email: '', contact_person: '', phone: '' });
      setEditingTenant(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update tenant');
    }
  };

  const openEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setTenantForm({
      property_id: tenant.property_id,
      company_name: tenant.company_name,
      gstin: tenant.gstin,
      billing_address: tenant.billing_address,
      email: tenant.email,
      contact_person: tenant.contact_person || '',
      phone: tenant.phone || ''
    });
    setShowTenantModal(true);
  };

  const handleArchiveTenant = async (tenantId) => {
    setConfirmDialog({
      open: true,
      title: 'Archive Tenant',
      description: 'Are you sure you want to archive this tenant? They will be marked as inactive but their history will be preserved.',
      variant: 'default',
      confirmText: 'Archive',
      onConfirm: async () => {
        try {
          await api.patch(`/rentals/tenants/${tenantId}/archive`);
          toast.success('Tenant archived');
          fetchData();
        } catch (error) {
          toast.error('Failed to archive tenant');
        }
      }
    });
  };

  const handleToggleReminders = async (tenantId, currentlyDisabled) => {
    try {
      await api.patch(`/rentals/tenants/${tenantId}/toggle-reminders?disable=${!currentlyDisabled}`);
      toast.success(currentlyDisabled ? 'Reminders enabled' : 'Reminders disabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to update reminder settings');
    }
  };

  // Invoice handlers
  const handleGenerateInvoice = async () => {
    try {
      const tenant = tenants.find(t => t.id === invoiceForm.tenant_id);
      // Get per-month rent (either from form or from property)
      const perMonthRent = invoiceForm.base_amount 
        ? parseFloat(invoiceForm.base_amount) 
        : (tenant ? properties.find(p => p.id === tenant.property_id)?.base_rent : 0);
      // Total base amount is per-month rent × number of months
      const baseAmount = perMonthRent * selectedMonths.length;
      
      const res = await api.post('/rentals/invoices/generate', {
        tenant_id: invoiceForm.tenant_id,
        months: selectedMonths,
        base_amount: baseAmount,
        invoice_no: invoiceForm.invoice_no || null,
        invoice_date: invoiceForm.invoice_date || null,
        notes: invoiceForm.notes || null
      });
      toast.success(`Invoice ${res.data.invoice_no} generated!`);
      setShowInvoiceModal(false);
      setInvoiceForm({ tenant_id: '', invoice_no: '', invoice_date: '', months: [], base_amount: '', sgst_rate: '9', cgst_rate: '9', notes: '' });
      setSelectedMonths([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  const handleUpdateInvoice = async () => {
    if (!editingInvoice) return;
    try {
      const updateData = {
        tenant_id: invoiceForm.tenant_id,
        invoice_no: invoiceForm.invoice_no,
        alias_invoice_no: invoiceForm.alias_invoice_no,
        invoice_date: invoiceForm.invoice_date,
        base_amount: parseFloat(invoiceForm.base_amount),
        sgst_rate: parseFloat(invoiceForm.sgst_rate || '9'),
        cgst_rate: parseFloat(invoiceForm.cgst_rate || '9'),
        months: selectedMonths,
        notes: invoiceForm.notes
      };
      
      await api.patch(`/rentals/invoices/${editingInvoice.id}`, updateData);
      toast.success('Invoice updated successfully');
      
      // Show preview after update
      const invoiceId = editingInvoice.id;
      setShowEditInvoiceModal(false);
      setEditingInvoice(null);
      setInvoiceForm({ tenant_id: '', invoice_no: '', alias_invoice_no: '', invoice_date: '', months: [], base_amount: '', sgst_rate: '9', cgst_rate: '9', notes: '' });
      setSelectedMonths([]);
      fetchData();
      
      // Auto-open preview
      setTimeout(async () => {
        await handleViewInvoice(invoiceId);
      }, 500);
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handleUpdateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      await api.patch(`/rentals/invoices/${invoiceId}/status?status=${newStatus}`);
      toast.success(`Invoice marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Invoice',
      description: 'Are you sure you want to delete this invoice? This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await api.delete(`/rentals/invoices/${invoiceId}`);
          toast.success('Invoice deleted');
          fetchData();
        } catch (error) {
          toast.error('Failed to delete invoice');
        }
      }
    });
  };

  const openEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    // Parse invoice date from created_at or invoice_date
    let invoiceDate = '';
    if (invoice.invoice_date) {
      invoiceDate = invoice.invoice_date.split('T')[0];
    } else if (invoice.created_at) {
      invoiceDate = invoice.created_at.split('T')[0];
    }
    setInvoiceForm({
      tenant_id: invoice.tenant_id,
      invoice_no: invoice.invoice_no || '',
      alias_invoice_no: invoice.alias_invoice_no || '',
      invoice_date: invoiceDate,
      base_amount: invoice.financials?.base_amount?.toString() || '',
      sgst_rate: invoice.financials?.sgst_rate?.toString() || '9',
      cgst_rate: invoice.financials?.cgst_rate?.toString() || '9',
      notes: invoice.notes || ''
    });
    setSelectedMonths(invoice.months || []);
    setShowEditInvoiceModal(true);
  };

  const handleUploadHistorical = async () => {
    try {
      const months = historicalForm.months.split(',').map(m => m.trim()).filter(m => m);
      await api.post('/rentals/invoices/upload-historical', {
        tenant_id: historicalForm.tenant_id,
        invoice_no: historicalForm.invoice_no || null,
        invoice_date: historicalForm.invoice_date,
        months: months,
        base_amount: parseFloat(historicalForm.base_amount),
        total_amount: parseFloat(historicalForm.total_amount),
        notes: historicalForm.notes
      });
      toast.success('Historical invoice uploaded successfully');
      setShowHistoricalModal(false);
      setHistoricalForm({ tenant_id: '', invoice_no: '', invoice_date: '', months: '', base_amount: '', total_amount: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload historical invoice');
    }
  };

  // View invoice - shows HTML preview first
  const handleViewInvoice = async (invoiceId) => {
    try {
      const res = await api.get(`/rentals/invoices/${invoiceId}/preview`);
      setPreviewData(res.data);
      setPdfData(null); // Clear any existing PDF data
      setShowPdfPreview(true);
    } catch (error) {
      toast.error('Failed to load invoice');
    }
  };

  // Legacy function name for compatibility
  const handleViewPdf = handleViewInvoice;

  // Generate PDF for download
  const handleDownloadPdf = async () => {
    if (!previewData) return;
    try {
      toast.info('Generating PDF...');
      const res = await api.get(`/rentals/invoices/${previewData.invoice.id}/pdf`);
      // Convert base64 to blob for more reliable download
      const byteCharacters = atob(res.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Direct download PDF by invoice ID (for dashboard quick actions)
  const handleDownloadPdfDirect = async (invoiceId) => {
    try {
      toast.info('Generating PDF...');
      const res = await api.get(`/rentals/invoices/${invoiceId}/pdf`);
      // Convert base64 to blob for more reliable download
      const byteCharacters = atob(res.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Print invoice - generates PDF and opens print dialog
  const handlePrintPdf = async () => {
    if (!previewData) return;
    try {
      toast.info('Preparing print...');
      const res = await api.get(`/rentals/invoices/${previewData.invoice.id}/pdf`);
      const byteCharacters = atob(res.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Open in new window for printing
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => printWindow.print(), 500);
        };
      } else {
        toast.error('Popup blocked. Please allow popups for this site.');
      }
    } catch (error) {
      console.error('PDF print error:', error);
      toast.error('Failed to generate PDF for printing');
    }
  };

  // Direct print PDF by invoice ID (for dashboard quick actions)
  const handlePrintPdfDirect = async (invoiceId) => {
    try {
      toast.info('Preparing print...');
      const res = await api.get(`/rentals/invoices/${invoiceId}/pdf`);
      const byteCharacters = atob(res.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Open in new window for printing
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => printWindow.print(), 500);
        };
      } else {
        toast.error('Popup blocked. Please allow popups for this site.');
      }
    } catch (error) {
      console.error('PDF print error:', error);
      toast.error('Failed to generate PDF for printing');
    }
  };

  const handleSendEmail = async (invoiceId) => {
    setConfirmDialog({
      open: true,
      title: 'Send Invoice Email',
      description: 'Send this invoice via email to the tenant? Make sure SMTP settings are configured.',
      variant: 'default',
      confirmText: 'Send Email',
      onConfirm: async () => {
        try {
          await api.post(`/rentals/invoices/${invoiceId}/send-email`);
          toast.success('Invoice sent via email!');
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to send email');
        }
      }
    });
  };

  // Generate all invoices for active tenants (with duplicate checking)
  const handleGenerateAllInvoices = async (dryRun = false) => {
    setGeneratingInvoices(true);
    try {
      const res = await api.post(`/rentals/agent/generate-invoices?dry_run=${dryRun}&skip_distribution=true`);
      const result = res.data;
      setGenerateResult(result);
      setShowGenerateResult(true);
      
      if (result.status === 'complete') {
        const generated = result.phases?.execution?.generated_invoices || [];
        if (dryRun) {
          toast.success(`Dry run complete: ${generated.length} invoice(s) would be generated`);
        } else {
          toast.success(`Generated ${generated.length} invoice(s) successfully!`);
          // Small delay to ensure DB has committed, then refresh data
          if (generated.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
            await fetchData();
            // Switch to invoices tab to show newly generated invoices
            setActiveTab('invoices');
          }
        }
      } else if (result.status === 'paused_for_review') {
        toast.warning(result.message || 'Some issues need attention');
      } else {
        toast.info(result.message || 'No invoices to generate');
      }
    } catch (error) {
      console.error('Error generating invoices:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoices');
    } finally {
      setGeneratingInvoices(false);
    }
  };

  // Note: handleUpdateInvoiceStatus is used for status updates

  // Month options for invoice
  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = -3; i <= 3; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      months.push(date.toLocaleString('en-US', { month: 'long', year: 'numeric' }));
    }
    return months;
  };

  const toggleMonth = (month) => {
    setSelectedMonths(prev => 
      prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Home className="text-primary" size={24} />
              Rental Management
            </h1>
            <p className="text-sm text-gray-500">Manage properties, tenants & GST invoices</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => { setEditingProperty(null); setPropertyForm({ name: '', address: '', base_rent: '' }); setShowPropertyModal(true); }}>
              <Plus size={14} className="mr-1" /> Property
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setEditingTenant(null); setTenantForm({ property_id: '', company_name: '', gstin: '', billing_address: '', email: '', contact_person: '', phone: '' }); setShowTenantModal(true); }}>
              <Plus size={14} className="mr-1" /> Tenant
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowHistoricalModal(true)}>
              <Upload size={14} className="mr-1" /> Upload Old
            </Button>
            <Button size="sm" onClick={() => { setShowInvoiceModal(true); fetchNextInvoiceNumber(); }}>
              <FileText size={14} className="mr-1" /> New Invoice
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 text-purple-700 hover:bg-purple-100"
              onClick={() => handleGenerateAllInvoices(false)}
              disabled={generatingInvoices}
              data-testid="generate-all-invoices-btn"
            >
              {generatingInvoices ? (
                <Loader2 size={14} className="mr-1 animate-spin" />
              ) : (
                <Sparkles size={14} className="mr-1" />
              )}
              {generatingInvoices ? 'Generating...' : 'Generate All'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b pb-2 overflow-x-auto">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: <IndianRupee size={14} /> },
            { id: 'invoices', label: 'Invoices', icon: <FileText size={14} /> },
            { id: 'properties', label: 'Properties', icon: <Building2 size={14} /> },
            { id: 'tenants', label: 'Tenants', icon: <Users size={14} /> },
            { id: 'settings', label: 'Settings', icon: <Edit2 size={14} /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-t-lg transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="card p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <div className="text-xs text-green-600 font-medium">Rent Collected (FY)</div>
                <div className="text-xl font-bold text-green-700">{formatCurrency(dashboard.financials.total_rent_collected_fy)}</div>
              </div>
              <div className="card p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
                <div className="text-xs text-blue-600 font-medium">GST Collected (FY)</div>
                <div className="text-xl font-bold text-blue-700">{formatCurrency(dashboard.financials.total_gst_collected_fy)}</div>
              </div>
              <div className="card p-4 bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200">
                <div className="text-xs text-yellow-600 font-medium">Pending Dues</div>
                <div className="text-xl font-bold text-yellow-700">{formatCurrency(dashboard.financials.pending_dues)}</div>
              </div>
              <div className="card p-4 bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
                <div className="text-xs text-purple-600 font-medium">Monthly GST Liability</div>
                <div className="text-xl font-bold text-purple-700">{formatCurrency(dashboard.financials.monthly_gst_liability)}</div>
              </div>
            </div>

            {/* Property Summary */}
            <div className="card p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Building2 size={16} /> Property Overview
              </h3>
              <div className="flex gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{dashboard.properties.total}</div>
                  <div className="text-xs text-gray-500">Total Properties</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{dashboard.properties.occupied}</div>
                  <div className="text-xs text-gray-500">Occupied</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-400">{dashboard.properties.vacant}</div>
                  <div className="text-xs text-gray-500">Vacant</div>
                </div>
              </div>
            </div>

            {/* Recent Invoices */}
            <div className="card p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FileText size={16} /> Recent Invoices
              </h3>
              <div className="space-y-2">
                {invoices.slice(0, 5).map(inv => (
                  <div key={inv.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium text-sm">{inv.invoice_no}</div>
                      <div className="text-xs text-gray-500">{inv.tenant_name} • {inv.months?.join(', ')}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-sm">{formatCurrency(inv.financials?.total)}</span>
                      <StatusBadge status={inv.status} />
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleViewInvoice(inv.id)} title="View" className="h-7 w-7 p-0">
                          <Eye size={12} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPdfDirect(inv.id)} title="Download PDF" className="h-7 w-7 p-0">
                          <Download size={12} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handlePrintPdfDirect(inv.id)} title="Print" className="h-7 w-7 p-0">
                          <Printer size={12} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {properties.map(prop => (
              <div key={prop.id} className="card p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold">{prop.name}</h3>
                  <button
                    onClick={() => handleTogglePropertyStatus(prop.id, !prop.is_occupied)}
                    className={`px-2 py-0.5 rounded-full text-xs cursor-pointer hover:opacity-80 transition-opacity ${prop.is_occupied ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                    title="Click to toggle status"
                    data-testid={`property-status-${prop.id}`}
                  >
                    {prop.is_occupied ? 'Occupied' : 'Available'}
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-3">{prop.address}</p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-400">Base Rent</div>
                    <div className="font-bold text-primary">{formatCurrency(prop.base_rent)}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditProperty(prop)} title="Edit">
                      <Edit2 size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteProperty(prop.id)} title="Delete" className="text-red-500 hover:text-red-700">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {properties.length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                No properties yet. Click &quot;+ Property&quot; to add one.
              </div>
            )}
          </div>
        )}

        {/* Tenants Tab */}
        {activeTab === 'tenants' && (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold">Company</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold">Property</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold">GSTIN</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold">Email</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold">Status</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map(tenant => (
                  <tr key={tenant.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium">{tenant.company_name}</div>
                      {tenant.contact_person && <div className="text-xs text-gray-500">{tenant.contact_person}</div>}
                    </td>
                    <td className="py-3 px-4 text-sm">{tenant.property_name}</td>
                    <td className="py-3 px-4 text-sm font-mono">{tenant.gstin}</td>
                    <td className="py-3 px-4 text-sm">{tenant.email}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${tenant.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {tenant.is_active ? 'Active' : 'Archived'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditTenant(tenant)} title="Edit">
                          <Edit2 size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleToggleReminders(tenant.id, tenant.reminders_disabled)} 
                          title={tenant.reminders_disabled ? "Enable Reminders" : "Disable Reminders"}
                          className={tenant.reminders_disabled ? "text-gray-400" : "text-blue-600"}
                        >
                          {tenant.reminders_disabled ? <BellOff size={14} /> : <Bell size={14} />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleArchiveTenant(tenant.id)} disabled={!tenant.is_active} title="Archive">
                          <Archive size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tenants.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No tenants yet. Click &quot;+ Tenant&quot; to add one.
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-4">
            {/* Search and Sort Controls */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search invoices by number, tenant, or month..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  data-testid="invoice-search"
                />
                <svg className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Sort by:</span>
                <select
                  className="px-3 py-2 border rounded-lg text-sm"
                  value={`${invoiceSort.field}-${invoiceSort.order}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setInvoiceSort({ field, order });
                  }}
                  data-testid="invoice-sort"
                >
                  <option value="invoice_date-desc">Date (Newest)</option>
                  <option value="invoice_date-asc">Date (Oldest)</option>
                  <option value="invoice_no-desc">Invoice # (High)</option>
                  <option value="invoice_no-asc">Invoice # (Low)</option>
                  <option value="tenant_name-asc">Tenant (A-Z)</option>
                  <option value="tenant_name-desc">Tenant (Z-A)</option>
                  <option value="total-desc">Amount (High)</option>
                  <option value="total-asc">Amount (Low)</option>
                </select>
              </div>
            </div>

            {/* Invoices Table */}
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Invoice #</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Tenant</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Period</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold">Base</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold">GST</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold">Total</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold">Status</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices
                    .filter(inv => {
                      if (!invoiceSearch) return true;
                      const search = invoiceSearch.toLowerCase();
                      return (
                        inv.invoice_no?.toLowerCase().includes(search) ||
                        inv.tenant_name?.toLowerCase().includes(search) ||
                        inv.months?.some(m => m.toLowerCase().includes(search))
                      );
                    })
                    .sort((a, b) => {
                      const { field, order } = invoiceSort;
                      let aVal, bVal;
                      
                      if (field === 'invoice_date') {
                        aVal = new Date(a.invoice_date || a.created_at || 0);
                        bVal = new Date(b.invoice_date || b.created_at || 0);
                      } else if (field === 'total') {
                        aVal = a.financials?.total || 0;
                        bVal = b.financials?.total || 0;
                      } else if (field === 'invoice_no') {
                        // Extract serial number for proper sorting
                        const getSerial = (no) => {
                          const parts = no?.split('/') || [];
                          return parseInt(parts[parts.length - 1]) || 0;
                        };
                        aVal = getSerial(a.invoice_no);
                        bVal = getSerial(b.invoice_no);
                      } else {
                        aVal = a[field] || '';
                        bVal = b[field] || '';
                      }
                      
                      if (order === 'asc') {
                        return aVal > bVal ? 1 : -1;
                      }
                      return aVal < bVal ? 1 : -1;
                    })
                    .map(inv => (
                    <tr key={inv.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{inv.invoice_no}</td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(inv.invoice_date || inv.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-3 px-4 text-sm">{inv.tenant_name}</td>
                      <td className="py-3 px-4 text-sm">{inv.months?.join(', ')}</td>
                      <td className="py-3 px-4 text-sm text-right">{formatCurrency(inv.financials?.base_amount)}</td>
                      <td className="py-3 px-4 text-sm text-right text-gray-500">
                        {formatCurrency((inv.financials?.cgst || 0) + (inv.financials?.sgst || 0))}
                      </td>
                      <td className="py-3 px-4 text-sm text-right font-semibold">{formatCurrency(inv.financials?.total)}</td>
                      <td className="py-3 px-4 text-center">
                        <StatusDropdown invoiceId={inv.id} currentStatus={inv.status} onStatusChange={handleUpdateInvoiceStatus} />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewInvoice(inv.id)} title="View Invoice" data-testid={`view-invoice-${inv.id}`}>
                            <Eye size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditInvoice(inv)} title="Edit">
                            <Edit2 size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleSendEmail(inv.id)} title="Send Email">
                            <Send size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteInvoice(inv.id)} title="Delete" className="text-red-500 hover:text-red-700">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {invoices.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No invoices yet. Click &quot;New Invoice&quot; to create one.
              </div>
            )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Owner Information */}
            <div className="card p-6">
              <h3 className="font-semibold text-lg mb-4">Owner Information (appears on invoices)</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    value={ownerSettings.name}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">PAN</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    value={ownerSettings.pan}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, pan: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium">Address</label>
                  <textarea
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    rows={2}
                    value={ownerSettings.address}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, address: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">GSTIN</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    value={ownerSettings.gstin}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, gstin: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">GST Registration Date</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    placeholder="e.g., 01-07-2017"
                    value={ownerSettings.gst_registration_date}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, gst_registration_date: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div className="card p-6">
              <h3 className="font-semibold text-lg mb-4">Bank Account Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Bank Name</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    value={ownerSettings.bank_name}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, bank_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Branch</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    value={ownerSettings.branch}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, branch: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Account Name</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    value={ownerSettings.account_name}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, account_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Account Number</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    value={ownerSettings.account_number}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, account_number: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">IFSC Code</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    value={ownerSettings.ifsc_code}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, ifsc_code: e.target.value })}
                  />
                </div>
              </div>

              {/* Invoice Template Defaults */}
              <div className="mt-8 pt-6 border-t">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <FileText size={18} /> Invoice Template Defaults
                </h4>
                <p className="text-sm text-gray-500 mb-4">These defaults will be used when creating new invoices.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Default SGST Rate (%)</label>
                    <input
                      type="text" inputMode="decimal"
                     
                      className="w-full mt-1 px-3 py-2 border rounded-lg"
                      value={formatINR(ownerSettings.default_sgst_rate || '9')}
                      onChange={(e) => setOwnerSettings({ ...ownerSettings, default_sgst_rate: stripCommas(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Default CGST Rate (%)</label>
                    <input
                      type="text" inputMode="decimal"
                     
                      className="w-full mt-1 px-3 py-2 border rounded-lg"
                      value={formatINR(ownerSettings.default_cgst_rate || '9')}
                      onChange={(e) => setOwnerSettings({ ...ownerSettings, default_cgst_rate: stripCommas(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="text-sm font-medium">Default Invoice Notes</label>
                  <textarea
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    rows={2}
                    placeholder="Notes that will appear on all new invoices..."
                    value={ownerSettings.default_notes || ''}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, default_notes: e.target.value })}
                  />
                </div>
                <div className="mt-4">
                  <label className="text-sm font-medium">Invoice Terms & Conditions</label>
                  <textarea
                    className="w-full mt-1 px-3 py-2 border rounded-lg"
                    rows={3}
                    placeholder="1. Payment due within 15 days of invoice date.&#10;2. Please mention invoice number in payment reference."
                    value={ownerSettings.invoice_terms || ''}
                    onChange={(e) => setOwnerSettings({ ...ownerSettings, invoice_terms: e.target.value })}
                  />
                </div>
              </div>

              <div className="mt-6">
                <Button onClick={handleSaveOwnerSettings}>
                  Save Settings
                </Button>
              </div>
            </div>
            
            {/* Data Migration Tools */}
            <div className="card p-6">
              <h3 className="font-semibold text-lg mb-4">Data Migration Tools</h3>
              <p className="text-sm text-gray-600 mb-4">
                Use these tools to update existing invoice data after system upgrades.
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await api.post('/rentals/invoices/migrate-alias');
                      toast.success(`${res.data.message}`);
                      fetchData();
                    } catch (error) {
                      toast.error('Migration failed: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                >
                  Recalculate Alias Numbers
                </Button>
                <Button 
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await api.post('/rentals/invoices/migrate-due-dates');
                      toast.success(`${res.data.message}`);
                      fetchData();
                    } catch (error) {
                      toast.error('Migration failed: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                >
                  Add Missing Due Dates
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Alias format: GST/01/2025-2026 (per-tenant sequential by date)
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Property Modal */}
      <Dialog open={showPropertyModal} onOpenChange={setShowPropertyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingProperty ? 'Edit Property' : 'Add New Property'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Property Name</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="e.g., Signature Towers Unit"
                value={propertyForm.name}
                onChange={(e) => setPropertyForm({ ...propertyForm, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Address</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="Full property address"
                value={propertyForm.address}
                onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Base Rent (₹)</label>
              <input
                type="text" inputMode="decimal"
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="Monthly rent amount"
                value={formatINR(propertyForm.base_rent)}
                onChange={(e) => setPropertyForm({ ...propertyForm, base_rent: stripCommas(e.target.value) })}
              />
            </div>
            <Button className="w-full" onClick={editingProperty ? handleUpdateProperty : handleCreateProperty}>
              {editingProperty ? 'Update Property' : 'Create Property'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tenant Modal */}
      <Dialog open={showTenantModal} onOpenChange={setShowTenantModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTenant ? 'Edit Tenant' : 'Add New Tenant'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div>
              <label className="text-sm font-medium">Property</label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                value={tenantForm.property_id}
                onChange={(e) => setTenantForm({ ...tenantForm, property_id: e.target.value })}
              >
                <option value="">Select Property</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Company Name</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                value={tenantForm.company_name}
                onChange={(e) => setTenantForm({ ...tenantForm, company_name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">GSTIN</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="e.g., 37ACLFA6580E1Z7"
                value={tenantForm.gstin}
                onChange={(e) => setTenantForm({ ...tenantForm, gstin: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Billing Address</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                value={tenantForm.billing_address}
                onChange={(e) => setTenantForm({ ...tenantForm, billing_address: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <input
                type="email"
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                value={tenantForm.email}
                onChange={(e) => setTenantForm({ ...tenantForm, email: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Contact Person</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  value={tenantForm.contact_person}
                  onChange={(e) => setTenantForm({ ...tenantForm, contact_person: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Phone</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  value={tenantForm.phone}
                  onChange={(e) => setTenantForm({ ...tenantForm, phone: e.target.value })}
                />
              </div>
            </div>
            <Button className="w-full" onClick={editingTenant ? handleUpdateTenant : handleCreateTenant}>
              {editingTenant ? 'Update Tenant' : 'Add Tenant'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Generation Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate GST Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {nextInvoiceNo && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-xs text-blue-600">Next Invoice Number</div>
                <div className="font-bold text-blue-800">{nextInvoiceNo}</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Tenant</label>
                <select
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  value={invoiceForm.tenant_id}
                  onChange={(e) => {
                    setInvoiceForm({ ...invoiceForm, tenant_id: e.target.value });
                    const tenant = tenants.find(t => t.id === e.target.value);
                    if (tenant) {
                      const prop = properties.find(p => p.id === tenant.property_id);
                      if (prop) setInvoiceForm(prev => ({ ...prev, base_amount: prop.base_rent.toString() }));
                    }
                  }}
                >
                  <option value="">Select Tenant</option>
                  {tenants.filter(t => t.is_active).map(t => (
                    <option key={t.id} value={t.id}>{t.company_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Invoice Date</label>
                <input
                  type="date"
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  value={invoiceForm.invoice_date || new Date().toISOString().split('T')[0]}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })}
                  data-testid="new-invoice-date"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Invoice Number (optional - auto-generated if blank)</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder={nextInvoiceNo || "Auto-generated"}
                value={invoiceForm.invoice_no || ''}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_no: e.target.value })}
                data-testid="new-invoice-no"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Select Month(s)</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {getMonthOptions().map(month => (
                  <button
                    key={month}
                    onClick={() => toggleMonth(month)}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      selectedMonths.includes(month) 
                        ? 'bg-primary text-white border-primary' 
                        : 'bg-white text-gray-600 border-gray-300 hover:border-primary'
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Base Rent (₹)</label>
              <input
                type="text" inputMode="decimal"
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                value={formatINR(invoiceForm.base_amount ? parseFloat(invoiceForm.base_amount) * selectedMonths.length : '')}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, base_amount: (parseFloat(stripCommas(e.target.value)) / (selectedMonths.length || 1)).toString() })}
              />
              <p className="text-xs text-gray-500 mt-1">
                {selectedMonths.length > 0 && invoiceForm.base_amount && 
                  `₹${parseFloat(invoiceForm.base_amount).toLocaleString('en-IN')} × ${selectedMonths.length} month(s)`
                }
              </p>
            </div>
            
            {/* GST Preview */}
            {selectedMonths.length > 0 && invoiceForm.base_amount && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="text-sm font-medium text-gray-700">GST Calculation Preview</div>
                <div className="flex justify-between text-sm">
                  <span>Base Amount</span>
                  <span>₹{(parseFloat(invoiceForm.base_amount) * selectedMonths.length).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>SGST @ 9%</span>
                  <span>₹{Math.round(parseFloat(invoiceForm.base_amount) * selectedMonths.length * 0.09).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>CGST @ 9%</span>
                  <span>₹{Math.round(parseFloat(invoiceForm.base_amount) * selectedMonths.length * 0.09).toLocaleString('en-IN')}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-primary">
                    ₹{Math.round(parseFloat(invoiceForm.base_amount) * selectedMonths.length * 1.18).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium">Notes (Optional)</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="Any additional notes..."
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
              />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowInvoiceModal(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleGenerateInvoice} disabled={!invoiceForm.tenant_id || selectedMonths.length === 0}>
                <FileText size={14} className="mr-1" /> Generate Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Preview Modal - HTML view with Print/Download options */}
      <Dialog open={showPdfPreview} onOpenChange={setShowPdfPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Invoice Preview</DialogTitle>
          </DialogHeader>
          {/* Action buttons below header to avoid overlap */}
          <div className="flex gap-2 justify-end -mt-2 mb-2">
            <Button variant="outline" size="sm" onClick={handlePrintPdf} data-testid="print-pdf-btn">
              <Printer size={14} className="mr-1" /> Print PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPdf} data-testid="download-pdf-btn">
              <Download size={14} className="mr-1" /> Download PDF
            </Button>
          </div>
          {previewData && (
            <div className="overflow-y-auto max-h-[70vh] border rounded-lg bg-white">
              {/* HTML Invoice Preview */}
              <div className="p-6 space-y-4" data-testid="invoice-html-preview">
                {/* Header */}
                <div className="text-center border-b-2 border-blue-900 pb-3">
                  <h1 className="text-2xl font-bold text-blue-900 tracking-wide">TAX INVOICE</h1>
                  <p className="text-xs text-gray-500">GST Compliant Invoice</p>
                </div>

                {/* Owner Info */}
                <div className="bg-gray-50 p-3 rounded border-l-4 border-blue-900">
                  <div className="font-bold text-blue-900 text-lg">{previewData.owner.name}</div>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
                    <div>
                      <div className="text-xs text-gray-500 uppercase">Address</div>
                      <div className="font-medium">{previewData.owner.address}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 uppercase">GSTIN</div>
                      <div className="font-medium">{previewData.owner.gstin}</div>
                    </div>
                  </div>
                </div>

                {/* Invoice Meta */}
                <div className="flex justify-between items-center">
                  <div className="bg-blue-900 text-white px-4 py-2 rounded">
                    <div className="text-xs opacity-80">Invoice Number</div>
                    <div className="font-bold text-lg">{previewData.invoice.alias_invoice_no || previewData.invoice.invoice_no}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Invoice Date</div>
                    <div className="font-semibold">{previewData.invoice.date}</div>
                  </div>
                </div>

                {/* Bill To */}
                <div className="bg-gray-100 p-3 rounded">
                  <div className="text-xs text-gray-500 uppercase mb-1">Bill To</div>
                  <div className="font-bold text-blue-900">{previewData.tenant.company_name}</div>
                  <div className="text-sm text-gray-600">{previewData.tenant.billing_address}</div>
                  <span className="inline-block bg-gray-200 px-2 py-1 rounded text-sm mt-1">
                    GSTIN: {previewData.tenant.gstin}
                  </span>
                </div>

                {/* Invoice Table */}
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-900 text-white">
                      <th className="text-left py-2 px-3 text-sm">Description</th>
                      <th className="text-right py-2 px-3 text-sm">Amount (INR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 px-3">
                        Rent for the month(s) of <strong>{previewData.invoice.months_display}</strong>
                        {previewData.invoice.notes && (
                          <div className="text-xs text-gray-500 mt-1">{previewData.invoice.notes}</div>
                        )}
                      </td>
                      <td className="text-right py-2 px-3 font-semibold">
                        ₹{previewData.invoice.financials.base_amount.toLocaleString('en-IN')}
                      </td>
                    </tr>
                    <tr className="bg-gray-50 text-gray-600">
                      <td className="py-2 px-3 text-sm">SGST @ {previewData.invoice.financials.sgst_rate}%</td>
                      <td className="text-right py-2 px-3">₹{previewData.invoice.financials.sgst.toLocaleString('en-IN')}</td>
                    </tr>
                    <tr className="bg-gray-50 text-gray-600">
                      <td className="py-2 px-3 text-sm">CGST @ {previewData.invoice.financials.cgst_rate}%</td>
                      <td className="text-right py-2 px-3">₹{previewData.invoice.financials.cgst.toLocaleString('en-IN')}</td>
                    </tr>
                    <tr className="bg-blue-900 text-white">
                      <td className="py-3 px-3 font-bold">TOTAL AMOUNT</td>
                      <td className="text-right py-3 px-3 font-bold text-lg">
                        ₹{previewData.invoice.financials.total.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Amount in Words */}
                <div className="bg-amber-50 p-3 rounded border-l-4 border-amber-500">
                  <div className="text-xs text-amber-700 uppercase">Amount in Words</div>
                  <div className="font-semibold text-amber-900">{previewData.invoice.amount_in_words}</div>
                </div>

                {/* Bank Details */}
                <div className="bg-green-50 p-3 rounded border-l-4 border-green-500">
                  <div className="font-semibold text-green-800 mb-2">Bank Details for Payment</div>
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div>
                      <div className="text-xs text-gray-500">Bank Name</div>
                      <div className="font-medium">{previewData.owner.bank_name}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Account Name</div>
                      <div className="font-medium">{previewData.owner.account_name}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Account Number</div>
                      <div className="font-medium">{previewData.owner.account_number}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">IFSC Code</div>
                      <div className="font-medium">{previewData.owner.ifsc_code}</div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-between items-end text-xs text-gray-500 pt-4 border-t">
                  <div className="max-w-md">
                    <strong>Terms & Conditions:</strong><br />
                    {(previewData.owner.invoice_terms || "1. Payment due within 7 days of invoice date.\n2. Please mention invoice number in payment reference.").split('\n').map((line, i) => (
                      <span key={i}>{line}<br /></span>
                    ))}
                  </div>
                  <div className="text-center">
                    <div className="border-t border-gray-400 w-40 mb-1"></div>
                    <div className="font-semibold">{previewData.owner.name}</div>
                    <div>Authorized Signatory</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Historical Invoice Upload Modal */}
      <Dialog open={showHistoricalModal} onOpenChange={setShowHistoricalModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Historical Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div>
              <label className="text-sm font-medium">Tenant</label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                value={historicalForm.tenant_id}
                onChange={(e) => setHistoricalForm({ ...historicalForm, tenant_id: e.target.value })}
              >
                <option value="">Select Tenant</option>
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{t.company_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Invoice Number (Optional)</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="e.g., GST/01/2025-2026"
                value={historicalForm.invoice_no}
                onChange={(e) => setHistoricalForm({ ...historicalForm, invoice_no: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate</p>
            </div>
            <div>
              <label className="text-sm font-medium">Invoice Date</label>
              <input
                type="date"
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                value={historicalForm.invoice_date}
                onChange={(e) => setHistoricalForm({ ...historicalForm, invoice_date: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Months Covered</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                placeholder="e.g., October 2025, November 2025"
                value={historicalForm.months}
                onChange={(e) => setHistoricalForm({ ...historicalForm, months: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Comma-separated list of months</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Base Amount (₹)</label>
                <input
                  type="text" inputMode="decimal"
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  value={formatINR(historicalForm.base_amount)}
                  onChange={(e) => setHistoricalForm({ ...historicalForm, base_amount: stripCommas(e.target.value) })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Total Amount (₹)</label>
                <input
                  type="text" inputMode="decimal"
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  value={formatINR(historicalForm.total_amount)}
                  onChange={(e) => setHistoricalForm({ ...historicalForm, total_amount: stripCommas(e.target.value) })}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Notes (Optional)</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                value={historicalForm.notes}
                onChange={(e) => setHistoricalForm({ ...historicalForm, notes: e.target.value })}
              />
            </div>
            <Button className="w-full" onClick={handleUploadHistorical}>
              <Upload size={14} className="mr-1" /> Upload Invoice
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Modal - Full Edit Capability */}
      <Dialog open={showEditInvoiceModal} onOpenChange={setShowEditInvoiceModal}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto max-h-[70vh] pr-2">
            {/* Row 1: Invoice Numbers & Date */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">System ID</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-lg bg-gray-50 text-gray-500"
                  value={invoiceForm.invoice_no}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_no: e.target.value })}
                  data-testid="edit-invoice-no"
                  placeholder="INV-VI-SIG-2526-XX"
                />
                <span className="text-xs text-gray-400">Internal tracking</span>
              </div>
              <div>
                <label className="text-sm font-medium">Display Invoice No</label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-lg border-primary"
                  value={invoiceForm.alias_invoice_no}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, alias_invoice_no: e.target.value })}
                  data-testid="edit-alias-invoice-no"
                  placeholder="GST/01/2025-2026"
                />
                <span className="text-xs text-blue-600">Shown on invoice PDF</span>
              </div>
              <div>
                <label className="text-sm font-medium">Invoice Date</label>
                <input
                  type="date"
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  value={invoiceForm.invoice_date}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })}
                  data-testid="edit-invoice-date"
                />
              </div>
            </div>

            {/* Row 2: Tenant Selection */}
            <div>
              <label className="text-sm font-medium">Tenant</label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                value={invoiceForm.tenant_id}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, tenant_id: e.target.value })}
                data-testid="edit-tenant-select"
              >
                <option value="">Select Tenant</option>
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{t.company_name}</option>
                ))}
              </select>
            </div>

            {/* Row 3: Month Selection */}
            <div>
              <label className="text-sm font-medium">Select Month(s)</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {getMonthOptions().map(month => (
                  <button
                    key={month}
                    onClick={() => toggleMonth(month)}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      selectedMonths.includes(month) 
                        ? 'bg-primary text-white border-primary' 
                        : 'bg-white text-gray-600 border-gray-300 hover:border-primary'
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </div>
            </div>

            {/* Row 4: Amounts and GST Rates */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Base Amount (₹)</label>
                <input
                  type="text" inputMode="decimal"
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  value={formatINR(invoiceForm.base_amount)}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, base_amount: stripCommas(e.target.value) })}
                  data-testid="edit-base-amount"
                />
              </div>
              <div>
                <label className="text-sm font-medium">SGST Rate (%)</label>
                <input
                  type="text" inputMode="decimal"
                 
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  value={formatINR(invoiceForm.sgst_rate)}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, sgst_rate: stripCommas(e.target.value) })}
                  data-testid="edit-sgst-rate"
                />
              </div>
              <div>
                <label className="text-sm font-medium">CGST Rate (%)</label>
                <input
                  type="text" inputMode="decimal"
                 
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  value={formatINR(invoiceForm.cgst_rate)}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, cgst_rate: stripCommas(e.target.value) })}
                  data-testid="edit-cgst-rate"
                />
              </div>
            </div>
            
            {/* GST Preview */}
            {invoiceForm.base_amount && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="text-sm font-medium text-gray-700">GST Calculation Preview</div>
                <div className="flex justify-between text-sm">
                  <span>Base Amount</span>
                  <span>₹{parseFloat(invoiceForm.base_amount || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>SGST @ {invoiceForm.sgst_rate || 9}%</span>
                  <span>₹{Math.round(parseFloat(invoiceForm.base_amount || 0) * parseFloat(invoiceForm.sgst_rate || 9) / 100).toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>CGST @ {invoiceForm.cgst_rate || 9}%</span>
                  <span>₹{Math.round(parseFloat(invoiceForm.base_amount || 0) * parseFloat(invoiceForm.cgst_rate || 9) / 100).toLocaleString('en-IN')}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-primary">
                    ₹{Math.round(parseFloat(invoiceForm.base_amount || 0) * (1 + parseFloat(invoiceForm.sgst_rate || 9)/100 + parseFloat(invoiceForm.cgst_rate || 9)/100)).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="text-sm font-medium">Notes (Optional)</label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-lg"
                rows={2}
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                placeholder="Any additional notes for this invoice..."
                data-testid="edit-notes"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2 border-t">
              <Button variant="outline" className="flex-1" onClick={() => setShowEditInvoiceModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={() => editingInvoice && handleViewInvoice(editingInvoice.id)}
                data-testid="preview-btn"
              >
                <Eye size={14} className="mr-1" /> Preview
              </Button>
              <Button className="flex-1" onClick={handleUpdateInvoice} data-testid="update-invoice-btn">
                Update Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Generate Invoices Result Modal */}
      <Dialog open={showGenerateResult} onOpenChange={setShowGenerateResult}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={20} className="text-purple-600" />
              Invoice Generation Result
            </DialogTitle>
          </DialogHeader>
          {generateResult && (
            <div className="space-y-4">
              {/* Status Banner */}
              <div className={`p-3 rounded-lg ${
                generateResult.status === 'complete' ? 'bg-green-50 border border-green-200' :
                generateResult.status === 'paused_for_review' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <div className={`font-medium ${
                  generateResult.status === 'complete' ? 'text-green-700' :
                  generateResult.status === 'paused_for_review' ? 'text-yellow-700' :
                  'text-blue-700'
                }`}>
                  {generateResult.status === 'complete' && <CheckCircle size={16} className="inline mr-1" />}
                  {generateResult.status === 'paused_for_review' && <AlertCircle size={16} className="inline mr-1" />}
                  {generateResult.message}
                </div>
              </div>
              
              {/* Observation Phase Results */}
              {generateResult.phases?.observation && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700">Observation Phase</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-gray-50 p-2 rounded">
                      <span className="text-gray-500">Active Tenants:</span>{' '}
                      <span className="font-medium">{generateResult.phases.observation.active_tenants_count}</span>
                    </div>
                    <div className="bg-gray-50 p-2 rounded">
                      <span className="text-gray-500">Ready to Invoice:</span>{' '}
                      <span className="font-medium">{generateResult.phases.observation.ready_for_invoicing?.length || 0}</span>
                    </div>
                    {generateResult.phases.observation.tenants_without_email > 0 && (
                      <div className="col-span-2 bg-yellow-50 p-2 rounded text-yellow-700 text-xs">
                        <AlertCircle size={12} className="inline mr-1" />
                        {generateResult.phases.observation.tenants_without_email} tenant(s) without email - invoices can be printed
                      </div>
                    )}
                  </div>
                  
                  {/* Blocking Issues */}
                  {generateResult.phases.observation.blocking_issues?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 p-2 rounded">
                      <div className="text-sm font-medium text-red-700 mb-1">Blocking Issues:</div>
                      <ul className="text-xs text-red-600 list-disc pl-4">
                        {generateResult.phases.observation.blocking_issues.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Warnings */}
                  {generateResult.phases.observation.warnings?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 p-2 rounded">
                      <div className="text-sm font-medium text-amber-700 mb-1">Warnings:</div>
                      <ul className="text-xs text-amber-600 list-disc pl-4 max-h-24 overflow-y-auto">
                        {generateResult.phases.observation.warnings.map((warn, i) => (
                          <li key={i}>{warn}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              {/* Execution Phase Results */}
              {generateResult.phases?.execution && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700">
                    {generateResult.phases.execution.dry_run ? 'Preview (Dry Run)' : 'Generated Invoices'}
                  </div>
                  
                  {generateResult.phases.execution.generated_invoices?.length > 0 ? (
                    <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                      {generateResult.phases.execution.generated_invoices.map((inv, i) => (
                        <div key={i} className="p-2 flex justify-between items-center text-sm">
                          <div>
                            <div className="font-medium">{inv.tenant_name}</div>
                            <div className="text-xs text-gray-500">{inv.invoice_no}</div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-green-600">₹{inv.amount?.toLocaleString('en-IN')}</div>
                            {!inv.tenant_email && (
                              <div className="text-xs text-amber-500">No email</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 text-center py-4">
                      No invoices were generated
                    </div>
                  )}
                  
                  {/* Failed Invoices */}
                  {generateResult.phases.execution.failed_invoices?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 p-2 rounded">
                      <div className="text-sm font-medium text-red-700 mb-1">Failed to Generate:</div>
                      {generateResult.phases.execution.failed_invoices.map((fail, i) => (
                        <div key={i} className="text-xs text-red-600">
                          {fail.tenant_name}: {fail.error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowGenerateResult(false)}>
                  Close
                </Button>
                {generateResult.status === 'complete' && generateResult.phases?.execution?.generated_invoices?.length > 0 && (
                  <Button onClick={async () => { 
                    setShowGenerateResult(false); 
                    setActiveTab('invoices'); 
                    // Refresh data when viewing invoices
                    await fetchData();
                  }}>
                    View Invoices
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
      />
    </Layout>
  );
};

export default RentalManagement;
